import React from 'react';
import { Heart, ExternalLink, MapPin, Info, Calendar, BookOpen } from 'lucide-react';

/**
 * Engine 50 — Disability Resources & Caregiver Support box.
 *
 * Renders below the SOAP plan when a developmental-disability trigger fires
 * (ASD, Down Syndrome, cerebral palsy, intellectual disability, Fragile X,
 * Rett, global developmental delay).
 *
 * Source: backend `resourceBundles` payload from disability_resources_engine.
 * Non-clinical informational — separate lane from risk flags.
 *
 * Includes:
 *   - Region badge (US / India / Other)
 *   - Per-bundle title + resource list (name / what / source / external link)
 *   - Advocacy footnote (e.g. Autism Speaks vs ASAN context)
 *   - Disclaimer with last-verified date
 */
export default function PatientCaregiverResources({ resourceBundles }) {
  if (!resourceBundles || !resourceBundles.bundles || resourceBundles.bundles.length === 0) {
    return null;
  }

  const { region, bundles, condition_triggers: triggers, last_verified } = resourceBundles;

  return (
    <section
      role="region"
      aria-label="Patient and caregiver resources"
      className="mt-6 rounded-2xl border-2 border-violet-300 bg-violet-50 p-5"
      data-testid="resource-bundles-section"
    >
      <header className="flex items-start justify-between gap-3 mb-4 pb-3 border-b border-violet-200">
        <div className="flex items-start gap-3">
          <div className="flex-shrink-0 w-10 h-10 rounded-full bg-violet-700 text-white flex items-center justify-center">
            <Heart className="w-5 h-5" />
          </div>
          <div>
            <h3 className="text-lg font-extrabold text-violet-900">Patient &amp; Caregiver Resources</h3>
            <p className="text-xs text-violet-800 mt-0.5">
              Government programs + trusted nonprofits for the conditions in this encounter.
            </p>
          </div>
        </div>
        <div className="flex flex-col items-end gap-1 flex-shrink-0">
          <span className="inline-flex items-center gap-1 bg-white border-2 border-violet-300 rounded-full px-2.5 py-0.5 text-[10px] font-bold text-violet-900">
            <MapPin className="w-3 h-3" /> {region}
          </span>
          <span className="inline-flex items-center gap-1 text-[10px] text-violet-700">
            <Calendar className="w-3 h-3" /> Verified {last_verified}
          </span>
        </div>
      </header>

      {/* Trigger chips */}
      {triggers && triggers.length > 0 && (
        <div className="flex flex-wrap gap-1.5 mb-4">
          {triggers.map((t, i) => (
            <span
              key={i}
              className="text-[10px] font-bold uppercase tracking-wider bg-white border border-violet-300 text-violet-800 rounded-full px-2 py-0.5"
              data-testid={`resource-trigger-${t}`}
            >
              {t.replace(/_/g, ' ')}
            </span>
          ))}
        </div>
      )}

      {/* Bundles */}
      {bundles.map((bundle, bIdx) => (
        <article key={bundle.bundle_id || bIdx} className="mb-5 last:mb-0" data-testid={`resource-bundle-${bundle.bundle_id}`}>
          <h4 className="font-extrabold text-base text-violet-900 mb-3 flex items-center gap-2">
            <BookOpen className="w-4 h-4" /> {bundle.title}
          </h4>
          <ul className="space-y-2.5">
            {bundle.resources.map((r, rIdx) => (
              <li key={rIdx} className="bg-white rounded-lg border border-violet-200 p-3" data-testid="resource-item">
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <p className="font-bold text-sm text-slate-900">{r.name}</p>
                    <p className="text-xs text-slate-700 mt-0.5 leading-relaxed">{r.what}</p>
                    <p className="text-[10px] text-slate-500 mt-1">
                      <span className="font-semibold">Source:</span> {r.source}
                    </p>
                    {r.footnote && (
                      <p className="text-[10px] italic text-amber-700 mt-1">{r.footnote}</p>
                    )}
                  </div>
                  {r.url && (
                    <a
                      href={r.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex-shrink-0 inline-flex items-center gap-1 text-xs font-bold text-violet-700 hover:text-violet-900 bg-violet-50 hover:bg-violet-100 rounded-md px-2.5 py-1"
                      data-testid={`resource-link-${r.kind || 'item'}`}
                      aria-label={`Open ${r.name} in a new tab`}
                    >
                      Visit <ExternalLink className="w-3 h-3" />
                    </a>
                  )}
                </div>
              </li>
            ))}
          </ul>

          {bundle.advocacy_footnote && (
            <div className="mt-3 flex items-start gap-2 bg-amber-50 border border-amber-200 rounded-md p-2.5">
              <Info className="w-3.5 h-3.5 text-amber-700 mt-0.5 flex-shrink-0" />
              <p className="text-[11px] text-amber-900 leading-snug">{bundle.advocacy_footnote}</p>
            </div>
          )}
        </article>
      ))}

      {/* Disclaimer */}
      <footer className="mt-4 pt-3 border-t border-violet-200">
        <p className="text-[10px] text-violet-800 leading-snug" data-testid="resource-disclaimer">
          {bundles[0]?.disclaimer}
        </p>
      </footer>
    </section>
  );
}
